/* eslint-disable no-restricted-globals */
import { useState, useMemo, useCallback } from 'react';

import useForm from '../utils/useForm';
import createBillPaymentTableCell from '../../helpers/tables/createBillPaymentPayeeTableCell';
import LenderService from '../../services/LenderService';
import VendorService from '../../services/VendorService';
import BorrowerService from '../../services/BorrowerService';
import ServicerService from '../../services/ServicerService';

const today = new Date();

const lenderFetchOptions = {
  key: ['lenders'],
  queryFn: LenderService.getLenders,
  labelKey: 'name',
};

const vendorFetchOptions = {
  key: ['vendors'],
  // eslint-disable-next-line consistent-return
  queryFn: async () => {
    const vendors = await VendorService.getVendors({});

    if (!vendors) {
      return [];
    }

    return vendors.sort((a, b) => {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  },
  labelKey: 'name',
};

const borrowerFetchOptions = {
  key: ['borrowers'],
  queryFn: BorrowerService.getBorrowers,
  labelKey: (borrower) => {
    return borrower.TIN_type === 'EIN'
      ? borrower.entity_name
      : `${borrower.first_name} ${borrower.last_name}`;
  },
};

const servicerFetchOptions = {
  key: ['servicers'],
  queryFn: ServicerService.getServicers,
  labelKey: 'name',
};

const useBillPaymentForm = ({ onSubmit, values, alwaysEnabled, create }) => {
  const [payeeObject, setPayeeObject] = useState(null);

  const valuesWithAmountRemaining = useMemo(() => {
    if (!values) return values;

    return {
      ...values,
      amountRemaining: values.amount - (values._amount_applied?.amount || 0),
      payee: createBillPaymentTableCell(values.payee_object, values),
    };
  }, [values]);

  const currentFetchOptions = useMemo(() => {
    let fetchOptions = {};

    if (payeeObject === 'lender') {
      fetchOptions = lenderFetchOptions;
    } else if (payeeObject === 'vendor') {
      fetchOptions = vendorFetchOptions;
    } else if (payeeObject === 'borrower') {
      fetchOptions = borrowerFetchOptions;
    } else if (payeeObject === 'servicer') {
      fetchOptions = servicerFetchOptions;
    }
    return fetchOptions;
  }, [payeeObject]);

  const fields = useMemo(
    () => [
      {
        title: 'Create Bill Payment',
        fields: [
          {
            title: 'Number',
            name: 'pixel_number',
            type: 'text',
            readOnly: true,
          },
          {
            title: 'Date',
            name: 'date',
            type: 'date',
            required: true,
            readOnly:
              (values?.check_use_lob && values?.check_id) ||
              (values?.status !== 'unapplied' && !alwaysEnabled),
            defaultValue: today,
          },
          {
            title: 'Amount',
            name: 'amount',
            type: 'currency',
            readOnly: true,
            isHidden: () => !values?.id,
          },
          {
            title: 'Source',
            name: 'source',
            type: 'select',
            options: [
              { value: 'ACH', label: 'ACH' },
              { value: 'ACH direct', label: 'ACH direct' },
              { value: 'Check', label: 'Check' },
              { value: 'Outside', label: 'Outside' },
              { value: 'Previous Servicer', label: 'Previous Servicer' },
            ],
            required: true,
            readOnly:
              (values?.check_use_lob && values?.check_id) ||
              (values?.status !== 'unapplied' && !alwaysEnabled),
          },
          {
            title: 'Use Lob',
            name: 'check_use_lob',
            type: 'checkbox',
            isHidden: (vals) => vals.source !== 'Check',
            readOnly:
              (values?.check_use_lob && values?.check_id) ||
              (values?.status !== 'unapplied' && !alwaysEnabled),
          },
          {
            title: 'Reference',
            name: 'reference',
            type: 'text',
            isHidden: (vals) => vals.check_use_lob && !vals.reference,
            readOnly: values?.check_use_lob && values?.check_id,
          },
          {
            title: 'Use Bill.com',
            name: 'check_use_billcom',
            type: 'checkbox',
            isHidden: (vals) =>
              vals.source !== 'Check' || !values?.check_use_billcom,
            readOnly:
              (values?.status !== 'unapplied' && !alwaysEnabled) ||
              values?.billcom_sentpay_id,
          },
          {
            title: 'Check Priority',
            name: 'check_priority',
            type: 'select',
            options: (vals) => {
              if (vals.check_use_lob) {
                return [
                  {
                    value: 'Standard',
                    label: 'Standard',
                  },
                ];
              }

              return [
                {
                  value: 'Standard',
                  label: 'Standard',
                },
                {
                  value: 'USPS Priority',
                  label: 'USPS Priority',
                },
                {
                  value: 'UPS 1 day delivery',
                  label: 'UPS 1 day delivery',
                },
                {
                  value: 'UPS 2 day delivery',
                  label: 'UPS 2 day delivery',
                },
                {
                  value: 'UPS 3 day delivery',
                  label: 'UPS 3 day delivery',
                },
              ];
            },
            isHidden: (vals) => vals.source !== 'Check',
            readOnly:
              (values?.check_use_lob && values?.check_id) ||
              (values?.status !== 'unapplied' && !alwaysEnabled),
          },

          {
            title: 'Bill.com SentPay ID',
            name: 'billcom_sentpay_id',
            type: 'text',
            readOnly: true,
            isHidden: (vals) =>
              !vals._transfer_bills || vals.status !== 'applied',
          },
          {
            title: 'Payee',
            name: 'payee',
            type: 'text',
            readOnly: true,
            isHidden: (vals) => create || vals.status !== 'applied',
          },
          {
            title: 'Payee Type',
            name: 'payee_object',
            type: 'select',
            readOnly:
              (values?.check_use_lob && values?.check_id) ||
              (values?.status !== 'unapplied' && !alwaysEnabled),
            required: true,
            isHidden: (vals) => !create && vals.status !== 'unapplied',
            onChange: (value) => setPayeeObject(value),
            options: [
              {
                label: 'Lender',
                value: 'lender',
              },
              {
                label: 'Vendor',
                value: 'vendor',
              },
              {
                label: 'Borrower',
                value: 'borrower',
              },
              {
                label: 'Servicer',
                value: 'servicer',
              },
            ],
          },
          {
            title: 'Payee',
            name: 'payee_object_id',
            type: 'fetchableSelect',
            fetchOptions: currentFetchOptions,
            isHidden: (vals) => !create && vals.status !== 'unapplied',
            required: true,
          },
          {
            title: 'Paid From',
            name: 'paid_from',
            type: 'select',
            options: (vals) => {
              let options = [
                {
                  value: 'impound',
                  label: 'Impound',
                },
                {
                  value: 'clearing',
                  label: 'Clearing',
                },
                {
                  value: 'operating',
                  label: 'Operating',
                },
                {
                  value: 'outside',
                  label: 'Outside',
                },
              ];

              // Remove 'outside' option if Use Lob is selected
              if (vals.check_use_lob) {
                options = options.filter(
                  (option) => option.value !== 'outside'
                );
              }

              if (vals.payee_object === 'vendor') {
                options = options.filter(
                  (option) => option.value !== 'clearing'
                );
              } else if (
                vals.payee_object === 'lender' &&
                vals.source !== 'Previous Servicer'
              ) {
                options = options.filter(
                  (option) => option.value !== 'outside'
                );
              }

              return options;
            },
            required: true,
            readOnly:
              (values?.check_use_lob && values?.check_id) ||
              values?.billcom_sentpay_id,
          },
          {
            title: 'Paid To',
            name: 'paid_to',
            type: 'select',
            options: (vals) => {
              let options = [
                {
                  value: 'impound',
                  label: 'Impound',
                },
                {
                  value: 'clearing',
                  label: 'Clearing',
                },
                {
                  value: 'operating',
                  label: 'Operating',
                },
                {
                  value: 'custodial',
                  label: 'Custodial',
                },
                {
                  value: 'outside',
                  label: 'Outside',
                },
              ];

              if (
                vals.payee_object === 'vendor' ||
                vals.payee_object === 'borrower'
              ) {
                options = options.filter((option) => {
                  if (vals.source === 'Previous Servicer') {
                    return (
                      option.value === 'outside' || option.value === 'impound'
                    );
                  }

                  return option.value === 'outside';
                });
              } else if (vals.payee_object === 'lender') {
                options = options.filter((option) => {
                  if (vals.source === 'Previous Servicer') {
                    return (
                      option.value === 'outside' ||
                      option.value === 'custodial' ||
                      option.value === 'impound'
                    );
                  }

                  return (
                    option.value === 'outside' || option.value === 'custodial'
                  );
                });
              } else if (vals.payee_object === 'servicer') {
                options = options.filter((option) => {
                  if (vals.source === 'Previous Servicer') {
                    return option.value !== 'custodial';
                  }

                  return (
                    option.value !== 'custodial' && option.value !== 'outside'
                  );
                });
              }

              return options;
            },
            required: true,
            readOnly:
              (values?.check_use_lob && values?.check_id) ||
              values?.billcom_sentpay_id,
          },
          {
            title: 'Check or ACH direct status',
            type: 'select',
            name: 'check_status',
            options: [
              {
                value: 'pending',
                label: 'Pending',
              },
              {
                value: 'delivered',
                label: 'Delivered',
              },
              {
                value: 'cashed',
                label: 'Cashed',
              },
            ],
            isHidden: (vals) =>
              (vals.source !== 'Check' && vals.source !== 'ACH direct') ||
              vals.check_use_lob,
          },
        ],
      },
    ],
    [
      alwaysEnabled,
      create,
      currentFetchOptions,
      values?.billcom_sentpay_id,
      values?.check_use_billcom,
      values?.check_use_lob,
      values?.reference,
      values?.status,
      values?.id,
      values?.check_id,
    ]
  );

  // When the payee_object changes, we need to update the fetch options and reset the payee_object_id
  const onValuesChange = useCallback(
    (vals, setFieldValue) => {
      setPayeeObject(vals.payee_object);

      // If source !== 'Check', check_use_billcom, check_use_lob and check_priority should be null
      if (vals.source !== 'Check') {
        setFieldValue('check_use_billcom', '');
        setFieldValue('check_use_lob', '');
        setFieldValue('check_priority', '');
      }

      // If check_use_lob is selected, set check_priority to Standard
      if (vals.check_use_lob) {
        setFieldValue('check_priority', 'Standard');
      }

      if (!currentFetchOptions.key || !vals.payee_object) {
        return;
      }

      if (currentFetchOptions?.key?.[0] !== `${vals.payee_object}s`) {
        setFieldValue('payee_object_id', '');
      }

      // Reset paid_from if payee_object changes and current value is not in the new options
      if (
        (vals.payee_object === 'vendor' && vals.paid_from === 'clearing') ||
        (vals.payee_object === 'lender' &&
          vals.source !== 'Previous Servicer' &&
          vals.paid_from === 'outside')
      ) {
        setFieldValue('paid_from', '');
      }

      // Reset paid_to if payee_object changes and current value is not in the new options
      if (
        ((vals.payee_object === 'vendor' || vals.payee_object === 'borrower') &&
          vals.paid_to !== 'outside') ||
        (vals.payee_object === 'lender' &&
          vals.paid_to !== 'outside' &&
          vals.paid_to !== 'custodial') ||
        (vals.payee_object === 'servicer' &&
          (vals.paid_to === 'custodial' || vals.paid_to === 'outside'))
      ) {
        setFieldValue('paid_to', '');
      }
    },
    [currentFetchOptions]
  );

  const form = useForm({
    fields,
    onSubmit,
    values: valuesWithAmountRemaining,
    onlyValuesPresentInForm: false,
    onValuesChange,
  });

  return form;
};

export default useBillPaymentForm;
